import React from 'react';
import { isAuditTableWidget } from '../../utils/widgetHelpers';
import LabelStyleControls from './LabelStyleControls';
import {
    isUsingGlobalVariable,
    getVariablesByCategory,
    extractVariableName,
    getColorValue
} from '../../../../utils/variableResolver';

const StyleTab = ({ block, updateConfig, updateStyle, onResize, globalVariables, dataContext, theme }) => {
    // Helper to check if a property is currently managed by an indicator target
    const isIndicatorActive = (prop) => {
        const config = block.config || {};
        const template = config.styleTemplate || 'classic';
        const isFindings = block.widgetType === 'findings-status' || block.widgetType === 'single-finding-status';
        
        if (!isFindings) return false;

        if (prop === 'sfTextColor') {
            if (template === 'flexible') return !!config.indicatorLabel;
            if (template === 'classic') return config.indicatorTarget === 'text' || config.indicatorTarget === 'both' || !config.indicatorTarget;
            return false;
        }
        if (prop === 'sfLabelBgColor') {
            if (template === 'flexible') return !!config.indicatorLabelBg;
            if (template === 'classic') return config.indicatorTarget === 'label' || config.indicatorTarget === 'both' || !config.indicatorTarget; // Fallback for classic
            return false;
        }
        if (prop === 'sfIconColor') {
            if (template === 'flexible') return !!config.indicatorIcon;
            if (template === 'classic') return true; // Icon is always indicator in classic
            return false;
        }
        if (prop === 'sfItemBgColor') return !!config.indicatorBackground;
        if (prop === 'sfItemBorderColor') return !!config.indicatorBorder;
        return false;
    };

    return (
        <div className="property-group">
            {isAuditTableWidget(block.widgetType) && (
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <h4 style={{ marginTop: '0', marginBottom: '0.75rem' }}>Table Display</h4>
                    <div className="property-field">
                        <label>Display Mode</label>
                        <select
                            value={block.config.displayMode || 'basic'}
                            onChange={(e) => updateConfig('displayMode', e.target.value)}
                        >
                            <option value="basic">Basic (Simplified)</option>
                            <option value="advanced">Advanced (All Details)</option>
                        </select>
                        <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '0.75rem' }}>
                            Basic mode shows a cleaner, simplified view of AD data.
                        </small>
                    </div>
                </div>
            )}
            {!(block.elementType === 'header' || block.elementType === 'footer') && <h4>Appearance</h4>}
            {(block.elementType === 'header' || block.elementType === 'footer') && (
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #e5e7eb' }}>
                    <h4 style={{ marginTop: '0', marginBottom: '0.75rem' }}>Section Dimensions</h4>
                    <div className="property-field">
                        <label>Height (px)</label>
                        <input
                            type="number"
                            min="20"
                            max="500"
                            value={block.size?.height || 100}
                            onChange={(e) => {
                                const newHeight = parseInt(e.target.value) || 20;
                                if (onResize) {
                                    onResize(block.id, { ...block.size, height: newHeight }, null, { skipSnapping: true, skipConstraints: true });
                                }
                            }}
                        />
                        <small style={{ color: '#666', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                            Adjust the height of the {block.elementType}.
                        </small>
                    </div>
                </div>
            )}

            {/* Grid Item Container Styles - Moved to top for Scan Grids */}
            {block.type === 'widget' && (block.widgetType === 'infrastructure-scan-grid' || block.widgetType === 'managed-service-experience' || block.widgetType === 'security-scan-grid' || block.widgetType === 'cyber-security-threat-exposure' || block.widgetType === 'combined-scan-grid' || block.widgetType === 'advanced-scan-grid') && (
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>

                    {/* Grid Item Container Styles */}
                    <div className="property-subgroup" style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f9fafb', borderRadius: '4px' }}>
                        <label style={{ fontWeight: '600', display: 'block', marginBottom: '8px' }}>Grid Item Container</label>
                        <div className="property-field">
                            <label>Border Width (px)</label>
                            <input
                                type="number"
                                value={block.style.itemBorderWidth !== undefined ? block.style.itemBorderWidth : 0}
                                onChange={(e) => updateStyle('itemBorderWidth', parseInt(e.target.value))}
                            />
                        </div>
                        <div className="property-field">
                            <label>Border Radius (px)</label>
                            <input
                                type="number"
                                value={block.style.itemBorderRadius || 0}
                                onChange={(e) => updateStyle('itemBorderRadius', parseInt(e.target.value))}
                            />
                        </div>
                        <div className="property-field">
                            <label>Border Style</label>
                            <select
                                value={block.style.itemBorderStyle || 'solid'}
                                onChange={(e) => updateStyle('itemBorderStyle', e.target.value)}
                            >
                                <option value="solid">Solid</option>
                                <option value="dashed">Dashed</option>
                                <option value="dotted">Dotted</option>
                                <option value="double">Double</option>
                                <option value="none">None</option>
                            </select>
                        </div>
                        <div className="property-field" style={{ display: 'flex', alignItems: 'center', marginTop: '8px', marginBottom: '8px' }}>
                            <input
                                type="checkbox"
                                id="border-color-override"
                                checked={!!block.style.itemBorderColorOverride}
                                onChange={(e) => updateStyle('itemBorderColorOverride', e.target.checked)}
                                style={{ marginRight: '8px' }}
                            />
                            <label htmlFor="border-color-override" style={{ marginBottom: '0', cursor: 'pointer' }}>Use Custom Border Color</label>
                        </div>
                        {block.style.itemBorderColorOverride && (
                            <div className="property-field">
                                <label>Border Color</label>
                                <input
                                    type="color"
                                    value={block.style.itemBorderColor || '#000000'}
                                    onChange={(e) => updateStyle('itemBorderColor', e.target.value)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Findings Override Styling (Shared by Findings Status and Single Finding Status) */}
            {block.type === 'widget' && (block.widgetType === 'single-finding-status' || block.widgetType === 'findings-status') && (() => {
                // Get list of currently selected findings to populate the target dropdown
                const selectedFindings = block.widgetType === 'single-finding-status' 
                    ? (block.config?.selectedFindings || [])
                    : (() => {
                        // For findings-status, try to get all findings from dataContext (prop or block local)
                        const findingsSource = block.dataContext || dataContext;
                        const rawFindings = (findingsSource?.findings || findingsSource?.scanData?.findings || []);
                        return rawFindings.map(f => {
                            if (Array.isArray(f) && f.length > 0) return f[0];
                            if (f && typeof f === 'object') return f.name || f.category;
                            return f; // string or other
                        }).filter(Boolean);
                    })();

                // Current override target: '' means Global (All Findings)
                const overrideTarget = block.style._overrideTarget || '';

                const setOverrideTarget = (target) => {
                    updateStyle('_overrideTarget', target);
                };

                // Helper to get/set style value based on target
                const getStyleValue = (key, fallback) => {
                    if (overrideTarget) {
                        return block.style.findingOverrides?.[overrideTarget]?.[key] !== undefined
                            ? block.style.findingOverrides[overrideTarget][key]
                            : block.style[key] || fallback;
                    }
                    return block.style[key] || fallback;
                };

                const setStyleValue = (key, value) => {
                    if (overrideTarget) {
                        const currentOverrides = block.style.findingOverrides || {};
                        const targetOverrides = currentOverrides[overrideTarget] || {};
                        updateStyle('findingOverrides', {
                            ...currentOverrides,
                            [overrideTarget]: { ...targetOverrides, [key]: value }
                        });
                    } else {
                        updateStyle(key, value);
                    }
                };

                const isOverridden = (key) => {
                    return overrideTarget && block.style.findingOverrides?.[overrideTarget]?.[key] !== undefined;
                };

                const clearOverride = (key) => {
                    if (overrideTarget && block.style.findingOverrides?.[overrideTarget]) {
                         const currentOverrides = block.style.findingOverrides;
                         const targetOverrides = { ...currentOverrides[overrideTarget] };
                         delete targetOverrides[key];
                         updateStyle('findingOverrides', {
                             ...currentOverrides,
                             [overrideTarget]: targetOverrides
                         });
                    }
                };

                return (
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
                    <h4 style={{ marginTop: '0', marginBottom: '0.75rem' }}>Finding Item Styles</h4>

                    {/* Target Selector */}
                    <div className="property-field" style={{ paddingBottom: '10px', borderBottom: '1px dashed #e5e7eb', marginBottom: '15px' }}>
                        <label>Target Finding</label>
                        <select
                            value={overrideTarget}
                            onChange={(e) => setOverrideTarget(e.target.value)}
                            style={{ fontWeight: overrideTarget ? 'bold' : 'normal', color: overrideTarget ? '#2563eb' : 'inherit' }}
                        >
                            <option value="">All Findings (Global)</option>
                            {selectedFindings.length > 0 ? (
                                selectedFindings.map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))
                            ) : (
                                <option value="" disabled>No findings found in data context</option>
                            )}
                        </select>
                        <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '0.75rem' }}>
                            {overrideTarget ? `Editing style for "${overrideTarget}" only.` : 'Editing default style for all items.'}
                        </small>
                    </div>

                    {/* UI Grid Layout matches Widget Title styles - High Density */}
                    {/* Content Layout Section */}
                    <div style={{ marginBottom: '16px', padding: '10px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <label style={{ fontSize: '11px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.025em' }}>Item Content Placement</label>
                            {isOverridden('sfItemFlexDirection') && (
                                <button onClick={() => clearOverride('sfItemFlexDirection')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px' }}>Reset</button>
                            )}
                        </div>
                        <select
                            value={getStyleValue('sfItemFlexDirection', 'column')}
                            onChange={(e) => setStyleValue('sfItemFlexDirection', e.target.value)}
                            style={{ width: '100%', height: '32px', fontSize: '12px', padding: '0 8px', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white' }}
                        >
                            <option value="row">Row (Icon Left)</option>
                            <option value="column">Column (Icon Top)</option>
                            <option value="row-reverse">Row Reverse (Icon Right)</option>
                            <option value="column-reverse">Column Reverse (Icon Bottom)</option>
                        </select>
                    </div>

                    {/* Text Styling Section */}
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#4b5563', padding: '4px 0', borderBottom: '1px solid #e5e7eb', marginBottom: '10px', marginTop: '15px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Text Styling</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 8px', marginBottom: '15px' }}>
                        {/* Text Color */}
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, opacity: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    Text Color {isOverridden('sfTextColor') && <span style={{ color: '#2563eb' }}>*</span>}
                                    {isIndicatorActive('sfTextColor') && <span title="Managed by status color" style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>}
                                </label>
                                {(isOverridden('sfTextColor') || (!overrideTarget && getStyleValue('sfTextColor', '#ffffff') !== '#ffffff')) && !isIndicatorActive('sfTextColor') && (
                                    <button 
                                        onClick={() => overrideTarget ? clearOverride('sfTextColor') : updateStyle('sfTextColor', '#ffffff')} 
                                        style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                                    >Reset</button>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                    type="color"
                                    value={getColorValue(getStyleValue('sfTextColor', '#ffffff'), globalVariables)}
                                    onChange={(e) => setStyleValue('sfTextColor', e.target.value)}
                                    disabled={false} // Always allow manual override as requested
                                    style={{ width: '28px', height: '28px', padding: '2px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                                />
                                <input
                                    type="text"
                                    value={isIndicatorActive('sfTextColor') && !getStyleValue('sfTextColor', null) ? 'Auto (Status)' : getStyleValue('sfTextColor', '#ffffff')}
                                    onChange={(e) => setStyleValue('sfTextColor', e.target.value)}
                                    disabled={false} // Always allow manual override
                                    style={{ flex: 1, minWidth: 0, padding: '4px', height: '28px', fontSize: '11px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                                    placeholder="#ffffff"
                                />
                            </div>
                        </div>

                        {/* Label Background Color */}
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, opacity: isIndicatorActive('sfLabelBgColor') ? 0.6 : 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    Label Bg {isOverridden('sfLabelBgColor') && <span style={{ color: '#2563eb' }}>*</span>}
                                    {isIndicatorActive('sfLabelBgColor') && <span title="Managed by status color" style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>}
                                </label>
                                {(isOverridden('sfLabelBgColor') || (!overrideTarget && getStyleValue('sfLabelBgColor', 'transparent') !== 'transparent')) && !isIndicatorActive('sfLabelBgColor') && (
                                    <button 
                                        onClick={() => overrideTarget ? clearOverride('sfLabelBgColor') : updateStyle('sfLabelBgColor', 'transparent')} 
                                        style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                                    >Reset</button>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                    type="color"
                                    value={getColorValue(getStyleValue('sfLabelBgColor', '#ffffff'), globalVariables)}
                                    onChange={(e) => setStyleValue('sfLabelBgColor', e.target.value)}
                                    disabled={isIndicatorActive('sfLabelBgColor')}
                                    style={{ width: '28px', height: '28px', padding: '2px', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: isIndicatorActive('sfLabelBgColor') ? 'not-allowed' : 'default' }}
                                />
                                <input
                                    type="text"
                                    value={isIndicatorActive('sfLabelBgColor') ? 'Auto (Status)' : getStyleValue('sfLabelBgColor', 'transparent')}
                                    onChange={(e) => setStyleValue('sfLabelBgColor', e.target.value)}
                                    disabled={isIndicatorActive('sfLabelBgColor')}
                                    style={{ flex: 1, minWidth: 0, padding: '4px', height: '28px', fontSize: '11px', border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: isIndicatorActive('sfLabelBgColor') ? '#f3f4f6' : 'white' }}
                                    placeholder="transparent"
                                />
                            </div>
                        </div>

                        {/* Text Size */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>Size (px) {isOverridden('sfTextFontSize') && <span style={{ color: '#2563eb' }}>*</span>}</label>
                                {isOverridden('sfTextFontSize') && (
                                    <button onClick={() => clearOverride('sfTextFontSize')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}>Reset</button>
                                )}
                            </div>
                            <input
                                type="number"
                                min="8"
                                max="72"
                                value={getStyleValue('sfTextFontSize', 14)}
                                onChange={(e) => setStyleValue('sfTextFontSize', parseInt(e.target.value) || 14)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                            />
                        </div>

                        {/* Font Weight */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>Weight {isOverridden('sfTextFontWeight') && <span style={{ color: '#2563eb' }}>*</span>}</label>
                                {isOverridden('sfTextFontWeight') && (
                                    <button onClick={() => clearOverride('sfTextFontWeight')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}>Reset</button>
                                )}
                            </div>
                            <select
                                value={getStyleValue('sfTextFontWeight', '700')}
                                onChange={(e) => setStyleValue('sfTextFontWeight', e.target.value)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                            >
                                <option value="300">Light</option>
                                <option value="400">Normal</option>
                                <option value="600">Semi-Bold</option>
                                <option value="700">Bold</option>
                                <option value="800">Extra-Bold</option>
                                <option value="900">Black</option>
                            </select>
                        </div>

                        {/* Text Align */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>Align {isOverridden('sfTextTextAlign') && <span style={{ color: '#2563eb' }}>*</span>}</label>
                                {isOverridden('sfTextTextAlign') && (
                                    <button onClick={() => clearOverride('sfTextTextAlign')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}>Reset</button>
                                )}
                            </div>
                            <select
                                value={getStyleValue('sfTextTextAlign', (block.widgetType === 'findings-status' ? 'center' : 'left'))}
                                onChange={(e) => setStyleValue('sfTextTextAlign', e.target.value)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                            >
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                            </select>
                        </div>

                        {/* Font Style */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>Style {isOverridden('sfTextStyle') && <span style={{ color: '#2563eb' }}>*</span>}</label>
                                {isOverridden('sfTextStyle') && (
                                    <button onClick={() => clearOverride('sfTextStyle')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}>Reset</button>
                                )}
                            </div>
                            <select
                                value={getStyleValue('sfTextStyle', 'normal')}
                                onChange={(e) => setStyleValue('sfTextStyle', e.target.value)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                            >
                                <option value="normal">Normal</option>
                                <option value="italic">Italic</option>
                            </select>
                        </div>

                        {/* Decoration */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>Decoration {isOverridden('sfTextDecoration') && <span style={{ color: '#2563eb' }}>*</span>}</label>
                                {isOverridden('sfTextDecoration') && (
                                    <button onClick={() => clearOverride('sfTextDecoration')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}>Reset</button>
                                )}
                            </div>
                            <select
                                value={getStyleValue('sfTextDecoration', 'none')}
                                onChange={(e) => setStyleValue('sfTextDecoration', e.target.value)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                            >
                                <option value="none">None</option>
                                <option value="underline">Underline</option>
                                <option value="line-through">Strikethrough</option>
                            </select>
                        </div>

                        {/* Text Wrap */}
                        <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>Text Wrap {isOverridden('sfTextWrap') && <span style={{ color: '#2563eb' }}>*</span>}</label>
                                {isOverridden('sfTextWrap') && (
                                    <button onClick={() => clearOverride('sfTextWrap')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}>Reset</button>
                                )}
                            </div>
                            <select
                                value={getStyleValue('sfTextWrap', 'wrap')}
                                onChange={(e) => setStyleValue('sfTextWrap', e.target.value)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                            >
                                <option value="wrap">Wrap</option>
                                <option value="nowrap">No Wrap (Single Line)</option>
                            </select>
                        </div>
                    </div>

                    {/* Icon Styling Section */}
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#4b5563', padding: '4px 0', borderBottom: '1px solid #e5e7eb', marginBottom: '10px', marginTop: '15px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Icon Styling</div>
                    
                    {/* Custom Icon Override */}
                    {overrideTarget && (
                        <div className="property-field" style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500' }}>Custom Icon (URL) {isOverridden('customIcon') && <span style={{ color: '#2563eb', fontSize: '0.7rem' }}>(Overridden)</span>}</label>
                                {isOverridden('customIcon') && (
                                    <button 
                                        onClick={() => clearOverride('customIcon')}
                                        style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                                    >Reset</button>
                                )}
                            </div>
                            <input
                                type="text"
                                placeholder="https://example.com/icon.png"
                                value={getStyleValue('customIcon', '')}
                                onChange={(e) => setStyleValue('customIcon', e.target.value)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px', width: '100%' }}
                            />
                        </div>
                    )}

                    {/* Custom Label Override */}
                    {overrideTarget && (
                        <div className="property-field" style={{ marginBottom: '12px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500' }}>Custom Label {isOverridden('customLabel') && <span style={{ color: '#2563eb', fontSize: '0.7rem' }}>(Overridden)</span>}</label>
                                {isOverridden('customLabel') && (
                                    <button 
                                        onClick={() => clearOverride('customLabel')}
                                        style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                                    >Reset</button>
                                )}
                            </div>
                            <input
                                type="text"
                                placeholder={`e.g., ${overrideTarget}`}
                                value={getStyleValue('customLabel', '')}
                                onChange={(e) => setStyleValue('customLabel', e.target.value)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px', width: '100%' }}
                            />
                        </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 8px', marginBottom: '15px' }}>
                        {/* Icon Color */}
                        <div style={{ display: 'flex', flexDirection: 'column', opacity: isIndicatorActive('sfIconColor') ? 0.6 : 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>
                                    Icon Color {isOverridden('sfIconColor') && <span style={{ color: '#2563eb' }}>*</span>}
                                    {isIndicatorActive('sfIconColor') && <span title="Managed by status color" style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>}
                                </label>
                                {(isOverridden('sfIconColor') || (!overrideTarget && getStyleValue('sfIconColor', '#374151') !== '#374151')) && !isIndicatorActive('sfIconColor') && (
                                    <button 
                                        onClick={() => overrideTarget ? clearOverride('sfIconColor') : updateStyle('sfIconColor', '#374151')} 
                                        style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                                    >Reset</button>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                    type="color"
                                    value={getColorValue(getStyleValue('sfIconColor', '#374151'), globalVariables)}
                                    onChange={(e) => setStyleValue('sfIconColor', e.target.value)}
                                    disabled={isIndicatorActive('sfIconColor')}
                                    style={{ width: '28px', height: '28px', padding: '2px', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: isIndicatorActive('sfIconColor') ? 'not-allowed' : 'default' }}
                                />
                                <input
                                    type="text"
                                    value={isIndicatorActive('sfIconColor') ? 'Auto (Status)' : getStyleValue('sfIconColor', '#374151')}
                                    onChange={(e) => setStyleValue('sfIconColor', e.target.value)}
                                    disabled={isIndicatorActive('sfIconColor')}
                                    style={{ flex: 1, padding: '4px', height: '28px', fontSize: '11px', border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: isIndicatorActive('sfIconColor') ? '#f3f4f6' : 'white' }}
                                    placeholder="inherit"
                                />
                            </div>
                        </div>

                        {/* Icon Size */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>Icon Size (%)</label>
                            <input
                                type="number"
                                min="10"
                                max="200"
                                value={block.config.iconSize || 100}
                                onChange={(e) => updateConfig('iconSize', parseInt(e.target.value) || 100)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                            />
                        </div>
                    </div>

                    {/* Border & Radius Section */}
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#4b5563', padding: '4px 0', borderBottom: '1px solid #e5e7eb', marginBottom: '10px', marginTop: '15px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Border & Radius</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 8px', marginBottom: '15px' }}>
                        {/* Border Radius */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>Radius (px) {isOverridden('sfItemBorderRadius') && <span style={{ color: '#2563eb' }}>*</span>}</label>
                                {isOverridden('sfItemBorderRadius') && (
                                    <button onClick={() => clearOverride('sfItemBorderRadius')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}>Reset</button>
                                )}
                            </div>
                            <input
                                type="number"
                                min="0"
                                max="50"
                                value={getStyleValue('sfItemBorderRadius', 0)}
                                onChange={(e) => setStyleValue('sfItemBorderRadius', parseInt(e.target.value) || 0)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                            />
                        </div>

                        {/* Border Width */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>Width {isOverridden('sfItemBorderWidth') && <span style={{ color: '#2563eb' }}>*</span>}</label>
                                {isOverridden('sfItemBorderWidth') && (
                                    <button onClick={() => clearOverride('sfItemBorderWidth')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}>Reset</button>
                                )}
                            </div>
                            <input
                                type="number"
                                min="0"
                                max="20"
                                value={getStyleValue('sfItemBorderWidth', 0)}
                                onChange={(e) => setStyleValue('sfItemBorderWidth', parseInt(e.target.value) || 0)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                            />
                        </div>

                        {/* Border Style */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>Style {isOverridden('sfItemBorderStyle') && <span style={{ color: '#2563eb' }}>*</span>}</label>
                                {isOverridden('sfItemBorderStyle') && (
                                    <button onClick={() => clearOverride('sfItemBorderStyle')} style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}>Reset</button>
                                )}
                            </div>
                            <select
                                value={getStyleValue('sfItemBorderStyle', 'none')}
                                onChange={(e) => setStyleValue('sfItemBorderStyle', e.target.value)}
                                style={{ height: '28px', fontSize: '11px', padding: '0 8px', border: '1px solid #e5e7eb', borderRadius: '4px' }}
                            >
                                <option value="none">None</option>
                                <option value="solid">Solid</option>
                                <option value="dashed">Dashed</option>
                                <option value="dotted">Dotted</option>
                                <option value="double">Double</option>
                            </select>
                        </div>
                    </div>

                    {/* Border Color */}
                    <div className="property-field" style={{ marginBottom: '12px', opacity: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>
                                Border Color {isOverridden('sfItemBorderColor') && <span style={{ color: '#2563eb' }}>*</span>}
                                {isIndicatorActive('sfItemBorderColor') && <span title="Managed by status color" style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>}
                            </label>
                            {(isOverridden('sfItemBorderColor') || (!overrideTarget && getStyleValue('sfItemBorderColor', '#e5e7eb') !== '#e5e7eb')) && !isIndicatorActive('sfItemBorderColor') && (
                                <button 
                                    onClick={() => overrideTarget ? clearOverride('sfItemBorderColor') : updateStyle('sfItemBorderColor', '#e5e7eb')} 
                                    style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                                >Reset</button>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input
                                type="color"
                                value={getColorValue(getStyleValue('sfItemBorderColor', '#e5e7eb'), globalVariables)}
                                onChange={(e) => setStyleValue('sfItemBorderColor', e.target.value)}
                                disabled={isIndicatorActive('sfItemBorderColor')}
                                style={{ width: '40px', height: '32px', padding: '2px', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: isIndicatorActive('sfItemBorderColor') ? 'not-allowed' : 'default' }}
                            />
                            <input
                                type="text"
                                value={isIndicatorActive('sfItemBorderColor') ? 'Auto (Status)' : getStyleValue('sfItemBorderColor', '#e5e7eb')}
                                onChange={(e) => setStyleValue('sfItemBorderColor', e.target.value)}
                                disabled={isIndicatorActive('sfItemBorderColor')}
                                style={{ flex: 1, padding: '4px', height: '32px', fontSize: '11px', border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: isIndicatorActive('sfItemBorderColor') ? '#f3f4f6' : 'white' }}
                                placeholder="#e5e7eb"
                            />
                        </div>
                    </div>

                    {/* Background Styling Section */}
                    <div style={{ fontSize: '11px', fontWeight: '600', color: '#4b5563', padding: '4px 0', borderBottom: '1px solid #e5e7eb', marginBottom: '10px', marginTop: '15px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Background Styling</div>
                    {/* Item Background Color */}
                    <div className="property-field" style={{ marginBottom: '16px', opacity: isIndicatorActive('sfItemBgColor') ? 0.6 : 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label style={{ fontSize: '11px', fontWeight: '500', marginBottom: '2px' }}>
                                Item Background {isOverridden('sfItemBgColor') && <span style={{ color: '#2563eb' }}>*</span>}
                                {isIndicatorActive('sfItemBgColor') && <span title="Managed by status color" style={{ marginLeft: '4px', cursor: 'help' }}>ⓘ</span>}
                            </label>
                            {(isOverridden('sfItemBgColor') || (!overrideTarget && getStyleValue('sfItemBgColor', '#ffffff') !== '#ffffff')) && !isIndicatorActive('sfItemBgColor') && (
                                <button 
                                    onClick={() => overrideTarget ? clearOverride('sfItemBgColor') : updateStyle('sfItemBgColor', '#ffffff')} 
                                    style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                                >Reset</button>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            <input
                                type="color"
                                value={isIndicatorActive('sfItemBgColor') ? '#ffffff' : getStyleValue('sfItemBgColor', '#ffffff')}
                                onChange={(e) => setStyleValue('sfItemBgColor', e.target.value)}
                                disabled={isIndicatorActive('sfItemBgColor')}
                                style={{ width: '40px', height: '32px', padding: '2px', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: isIndicatorActive('sfItemBgColor') ? 'not-allowed' : 'default' }}
                            />
                            <input
                                type="text"
                                value={isIndicatorActive('sfItemBgColor') ? 'Auto (Status)' : getStyleValue('sfItemBgColor', '#ffffff')}
                                onChange={(e) => setStyleValue('sfItemBgColor', e.target.value)}
                                disabled={isIndicatorActive('sfItemBgColor')}
                                style={{ flex: 1, padding: '4px', height: '32px', fontSize: '11px', border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: isIndicatorActive('sfItemBgColor') ? '#f3f4f6' : 'white' }}
                                placeholder="transparent"
                            />
                        </div>
                    </div>

                    {/* Modern Style Controls (only if modern template is selected) */}
                    {(block.widgetType === 'single-finding-status' || block.widgetType === 'findings-status') && block.config?.styleTemplate === 'modern' && (
                        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px dashed #e5e7eb' }}>
                            <div className="property-field">
                                <label>Bar Radius (px)</label>
                                <input
                                    type="number" min="0" max="10"
                                    value={getStyleValue('sfModernBarRadius', 3)}
                                    onChange={(e) => setStyleValue('sfModernBarRadius', parseInt(e.target.value))}
                                />
                            </div>
                            <div className="property-field">
                                <label>Bar Height (px)</label>
                                <input
                                    type="number" min="1" max="50"
                                    value={getStyleValue('sfModernBarHeight', 4)}
                                    onChange={(e) => setStyleValue('sfModernBarHeight', parseInt(e.target.value))}
                                />
                            </div>
                        </div>
                    )}

                    {!overrideTarget && (
                        <button
                            className="btn btn-outline-secondary btn-sm w-100"
                            onClick={() => {
                                updateStyle('sfItemBgColor', '#ffffff');
                                updateStyle('sfTextColor', '#ffffff');
                                updateStyle('sfTextFontSize', 14);
                                updateStyle('sfTextFontWeight', '700');
                                updateStyle('sfTextStyle', 'normal');
                                updateStyle('sfTextDecoration', 'none');
                                updateStyle('sfTextTextAlign', (block.widgetType === 'findings-status' ? 'center' : 'left'));
                                updateStyle('customIcon', '');
                                updateStyle('sfModernBarRadius', 3);
                                updateStyle('sfModernBarHeight', 4);
                                updateStyle('findingOverrides', {});
                            }}
                            style={{ marginTop: '15px' }}
                        >
                            Reset All to Default
                        </button>
                    )}
                </div>
                );
            })()}

            {/* Widget Label Styles */}
            {!(block.elementType === 'header' || block.elementType === 'footer') && <LabelStyleControls block={block} updateConfig={updateConfig} dataContext={dataContext} theme={theme} />}

            {/* Shape, Image & Icon Alignment & Dimensions */}
            {block.type === 'element' && (block.elementType === 'shape' || block.elementType === 'image' || block.elementType === 'icon') && (
                <>
                    <h4 style={{ marginTop: '0', marginBottom: '0.75rem' }}>Layout & Alignment</h4>
                    
                    {/* Icon Selection moved to ContentTab */}

                    {/* Dimensions (Shape Only) */}
                    {block.elementType === 'shape' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            <div className="property-field">
                                <label>Width (px)</label>
                                <input 
                                    type="number" 
                                    placeholder="Auto"
                                    value={block.style.width ? parseInt(block.style.width) : ''}
                                    onChange={(e) => updateStyle('width', e.target.value ? `${e.target.value}px` : undefined)}
                                />
                            </div>
                             <div className="property-field">
                                <label>Height (px)</label>
                                <input 
                                    type="number" 
                                    placeholder="Auto"
                                    value={block.style.height ? parseInt(block.style.height) : ''}
                                    onChange={(e) => updateStyle('height', e.target.value ? `${e.target.value}px` : undefined)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Horizontal Alignment */}
                    <div className="property-field">
                        <label>Horizontal Alignment</label>
                        <select
                            value={block.config.align || 'center'}
                            onChange={(e) => updateConfig('align', e.target.value)}
                        >
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                        </select>
                    </div>

                    {/* Vertical Alignment */}
                     <div className="property-field">
                        <label>Vertical Alignment</label>
                        <select
                            value={block.config.verticalAlign || 'middle'}
                            onChange={(e) => updateConfig('verticalAlign', e.target.value)}
                        >
                            <option value="top">Top</option>
                            <option value="middle">Middle</option>
                            <option value="bottom">Bottom</option>
                        </select>
                    </div>
                </>
            )}


            {/* Typography Section - Consolidated font, color, and alignment controls */}
            {block.type === 'element' && (block.elementType === 'title' || block.elementType === 'text') && !(block.elementType === 'header' || block.elementType === 'footer') && (
                <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
                    <h4 style={{ margin: '0 0 1rem 0', fontSize: '15px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Text Styles
                    </h4>
                    
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '1.25rem', fontStyle: 'italic' }}>
                        Customize the appearance of titles, headers, and rows for this widget.
                    </div>

                    <div
                        className="property-subgroup"
                        style={{
                            marginBottom: '16px',
                            padding: '12px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '8px',
                            border: '1px solid #f3f4f6'
                        }}
                    >
                        {/* Section Header */}
                        <div style={{ 
                            fontWeight: '700', 
                            marginBottom: '12px', 
                            fontSize: '13px', 
                            color: '#111827',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <span>{block.elementType === 'title' ? 'Title Styling' : 'Body Text Styling'}</span>
                            {(block.style.textColor || block.style.labelFontSize || block.style.fontWeight || block.style.backgroundColor || block.config.align) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        updateStyle('textColor', undefined);
                                        updateStyle('labelFontSize', undefined);
                                        updateStyle('fontWeight', undefined);
                                        updateStyle('backgroundColor', undefined);
                                        updateConfig('align', undefined);
                                    }}
                                    style={{ 
                                        border: 'none', 
                                        background: 'none', 
                                        color: '#ef4444', 
                                        fontSize: '10px', 
                                        padding: '0',
                                        cursor: 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    Reset
                                </button>
                            )}
                        </div>

                        {/* Text Color */}
                        <div className="property-field" style={{ marginBottom: '12px' }}>
                            <label>Text Color</label>
                            <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="color"
                                        value={getColorValue(block.style.textColor || '#212529', globalVariables)}
                                        onChange={(e) => updateStyle('textColor', e.target.value)}
                                        style={{ width: '40px', height: '32px', padding: '2px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                                    />
                                    <input
                                        type="text"
                                        value={block.style.textColor || ''}
                                        onChange={(e) => updateStyle('textColor', e.target.value)}
                                        placeholder="#212529"
                                        style={{ flex: 1, height: '32px', fontSize: '12px', border: isUsingGlobalVariable(block.style.textColor) ? '1px dashed #3b82f6' : '1px solid #d1d5db' }}
                                    />
                                </div>
                                <select
                                    style={{ fontSize: '13px', height: '34px', marginTop: '4px', width: '100%', padding: '0 8px' }}
                                    value={isUsingGlobalVariable(block.style.textColor) ? block.style.textColor : ''}
                                    onChange={(e) => updateStyle('textColor', e.target.value || undefined)}
                                >
                                    <option value="">-- Static Color --</option>
                                    {Object.entries(getVariablesByCategory(globalVariables)).map(([category, vars]) => (
                                        <optgroup key={category} label={category}>
                                            {vars.map(v => (
                                                <option key={v.name} value={`var(--${v.name})`}>{v.name}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Dimensions Row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                            {/* Font Size */}
                            <div className="property-field">
                                <label>Font Size (px)</label>
                                <input
                                    type="number"
                                    min="8"
                                    max="72"
                                    value={block.style.labelFontSize || (block.elementType === 'title' ? 24 : 14)}
                                    onChange={(e) => updateStyle('labelFontSize', parseInt(e.target.value) || (block.elementType === 'title' ? 24 : 14))}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            {/* Font Weight */}
                            <div className="property-field">
                                <label>Weight</label>
                                <select
                                    value={block.style.fontWeight || ''}
                                    onChange={(e) => updateStyle('fontWeight', e.target.value)}
                                    style={{ width: '100%', height: '34px', fontSize: '13px', padding: '0 8px' }}
                                >
                                    <option value="">Default</option>
                                    <option value="300">Light</option>
                                    <option value="400">Normal</option>
                                    <option value="500">Medium</option>
                                    <option value="600">Semi-Bold</option>
                                    <option value="700">Bold</option>
                                </select>
                            </div>
                        </div>

                        {/* Alignment & Decoration Row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                             {/* Horizontal Alignment */}
                            <div className="property-field">
                                <label>Text Align</label>
                                <select
                                    value={block.config.align || 'left'}
                                    onChange={(e) => updateConfig('align', e.target.value)}
                                    style={{ width: '100%', height: '34px', fontSize: '13px', padding: '0 8px' }}
                                >
                                    <option value="left">Left</option>
                                    <option value="center">Center</option>
                                    <option value="right">Right</option>
                                </select>
                            </div>

                            {/* Vertical Alignment */}
                            <div className="property-field">
                                <label>Vertical Align</label>
                                <select
                                    value={block.config.verticalAlign || 'top'}
                                    onChange={(e) => updateConfig('verticalAlign', e.target.value)}
                                    style={{ width: '100%', height: '34px', fontSize: '13px', padding: '0 8px' }}
                                >
                                    <option value="top">Top</option>
                                    <option value="middle">Middle</option>
                                    <option value="bottom">Bottom</option>
                                </select>
                            </div>
                        </div>

                         {/* Background Color */}
                         <div className="property-field" style={{ marginBottom: '0' }}>
                            <label>Background Color</label>
                            <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <input
                                        type="color"
                                        value={getColorValue(block.style.backgroundColor || '#ffffff', globalVariables)}
                                        onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                                        style={{ width: '40px', height: '32px', padding: '2px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                                    />
                                    <input
                                        type="text"
                                        value={block.style.backgroundColor || ''}
                                        onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                                        placeholder="transparent"
                                        style={{ flex: 1, height: '32px', fontSize: '12px', border: isUsingGlobalVariable(block.style.backgroundColor) ? '1px dashed #3b82f6' : '1px solid #d1d5db' }}
                                    />
                                    {block.style.backgroundColor && (
                                        <button
                                            type="button"
                                            onClick={() => updateStyle('backgroundColor', undefined)}
                                            style={{ border: 'none', background: 'none', color: '#6b7280', fontSize: '18px', padding: '0 4px', cursor: 'pointer' }}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                                <select
                                    style={{ fontSize: '13px', height: '34px', marginTop: '4px', width: '100%', padding: '0 8px' }}
                                    value={isUsingGlobalVariable(block.style.backgroundColor) ? block.style.backgroundColor : ''}
                                    onChange={(e) => updateStyle('backgroundColor', e.target.value || undefined)}
                                >
                                    <option value="">-- Static Color --</option>
                                    {Object.entries(getVariablesByCategory(globalVariables)).map(([category, vars]) => (
                                        <optgroup key={category} label={category}>
                                            {vars.map(v => (
                                                <option key={v.name} value={`var(--${v.name})`}>{v.name}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* Chart Styling Controls for Chart Widgets */}
            {block.type === 'widget' && ['bar', 'line', 'pie', 'donut', 'radar', 'polar', 'bubble', 'scatter'].includes(block.widgetType) && (
                <>
                    <h4 style={{ marginTop: '0', marginBottom: '0.75rem' }}>Chart Display</h4>
                    
                    <div className="property-field">
                        <label>Main Chart Color</label>
                        <input
                            type="color"
                            value={block.style.mainChartColor || '#ff6384'}
                            onChange={(e) => updateStyle('mainChartColor', e.target.value)}
                        />
                    </div>
                    
                    <div className="property-field" style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                        <input
                            type="checkbox"
                            id="show-data-labels"
                            checked={block.style.showDataLabels !== undefined ? block.style.showDataLabels : (block.widgetType === 'bubble' ? false : true)}
                            onChange={(e) => updateStyle('showDataLabels', e.target.checked)}
                            style={{ marginRight: '8px' }}
                        />
                        <label htmlFor="show-data-labels" style={{ marginBottom: '0', cursor: 'pointer' }}>Show Values on Chart</label>
                    </div>

                    <div className="property-field" style={{ marginBottom: '12px' }}>
                        <label>Legend Information</label>
                        <select
                            value={block.style.legendMode || 'label'}
                            onChange={(e) => updateStyle('legendMode', e.target.value)}
                        >
                            <option value="label">Labels Only</option>
                            <option value="label-value">Labels & Values</option>
                            <option value="label-percent">Labels & Percentages</option>
                        </select>
                        <small style={{ display: 'block', marginTop: '4px', color: '#666', fontSize: '0.75rem' }}>
                            Choose what information appears in the chart legend.
                        </small>
                    </div>

                    <h4 style={{ marginTop: '1rem', marginBottom: '0.75rem' }}>Chart Colors</h4>
                    <div className="property-field">
                        <label>Custom Chart Colors</label>
                        <small style={{ display: 'block', marginBottom: '0.5rem', color: '#666', fontSize: '0.75rem' }}>
                            Leave empty to use default colors. Add one color per data point (comma-separated hex colors).
                        </small>
                        <input
                            type="text"
                            placeholder="e.g., #ef4444, #f97316, #eab308, #22c55e"
                            value={Array.isArray(block.style.chartColors) ? block.style.chartColors.join(', ') : (block.style.chartColors || '')}
                            onChange={(e) => {
                                const colors = e.target.value.split(',').map(c => c.trim()).filter(c => c);
                                updateStyle('chartColors', colors.length > 0 ? colors : []);
                            }}
                            style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                        />
                    </div>
                </>
            )}

            {/* Background Color */}
            <div className="property-field" style={{ marginBottom: '12px' }}>
                <label>Background Color</label>
                <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                            type="color"
                            value={getColorValue(block.style.backgroundColor || '#ffffff', globalVariables)}
                            onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                            style={{ width: '40px', height: '32px', padding: '2px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                        />
                        <input
                            type="text"
                            value={typeof block.style.backgroundColor === 'object' ? `var(${block.style.backgroundColor.value})` : (block.style.backgroundColor || '')}
                            onChange={(e) => updateStyle('backgroundColor', e.target.value)}
                            placeholder="#ffffff or transparent"
                            style={{ flex: 1, height: '32px', fontSize: '12px', border: isUsingGlobalVariable(block.style.backgroundColor) ? '1px dashed #3b82f6' : '1px solid #d1d5db' }}
                        />
                    </div>
                    <select
                        style={{ fontSize: '13px', height: '34px', marginTop: '4px', width: '100%', padding: '0 8px' }}
                        value={isUsingGlobalVariable(block.style.backgroundColor) 
                            ? (typeof block.style.backgroundColor === 'object' ? `var(${block.style.backgroundColor.value})` : block.style.backgroundColor) 
                            : ''}
                        onChange={(e) => updateStyle('backgroundColor', e.target.value || undefined)}
                    >
                        <option value="">-- Static Color --</option>
                        <optgroup label="Global Variables">
                            {getVariablesByCategory(globalVariables, 'colors').map(v => (
                                <option key={v.id} value={`var(${v.name})`}>
                                    {v.description} ({v.name})
                                </option>
                            ))}
                        </optgroup>
                    </select>
                </div>
            </div>

            <div className="property-field">
                <label>Border Width (px)</label>
                <input
                    type="number"
                    min="0"
                    max="10"
                    value={block.style.borderWidth !== undefined ? block.style.borderWidth : 0}
                    onChange={(e) => updateStyle('borderWidth', parseInt(e.target.value) || 0)}
                />
            </div>

            <div className="property-field">
                <label>Border Style</label>
                <select
                    value={block.style.borderStyle || 'solid'}
                    onChange={(e) => updateStyle('borderStyle', e.target.value)}
                    disabled={!block.style.borderWidth || block.style.borderWidth === 0}
                >
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                    <option value="dotted">Dotted</option>
                    <option value="double">Double</option>
                </select>
            </div>

            {/* Border Color with Variable Support */}
            {/* Border Color */}
            <div className="property-field" style={{ marginBottom: '12px' }}>
                <label>Border Color</label>
                <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                            type="color"
                            value={getColorValue(block.style.borderColor || '#e5e7eb', globalVariables)}
                            onChange={(e) => updateStyle('borderColor', e.target.value)}
                            style={{ width: '40px', height: '32px', padding: '2px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                            disabled={!block.style.borderWidth || block.style.borderWidth === 0}
                        />
                        <input
                            type="text"
                            value={typeof block.style.borderColor === 'object' ? `var(${block.style.borderColor.value})` : (block.style.borderColor || '')}
                            onChange={(e) => updateStyle('borderColor', e.target.value)}
                            placeholder="#e5e7eb or transparent"
                            style={{ flex: 1, height: '32px', fontSize: '12px', border: isUsingGlobalVariable(block.style.borderColor) ? '1px dashed #3b82f6' : '1px solid #d1d5db' }}
                            disabled={!block.style.borderWidth || block.style.borderWidth === 0}
                        />
                    </div>
                    <select
                        style={{ fontSize: '13px', height: '34px', marginTop: '4px', width: '100%', padding: '0 8px' }}
                        value={isUsingGlobalVariable(block.style.borderColor) 
                            ? (typeof block.style.borderColor === 'object' ? `var(${block.style.borderColor.value})` : block.style.borderColor) 
                            : ''}
                        onChange={(e) => updateStyle('borderColor', e.target.value || undefined)}
                        disabled={!block.style.borderWidth || block.style.borderWidth === 0}
                    >
                        <option value="">-- Static Color --</option>
                        <optgroup label="Global Variables">
                            {getVariablesByCategory(globalVariables, 'colors').map(v => (
                                <option key={v.id} value={`var(${v.name})`}>
                                    {v.description} ({v.name})
                                </option>
                            ))}
                        </optgroup>
                    </select>
                </div>
                {(!block.style.borderWidth || block.style.borderWidth === 0) && (
                    <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.75rem' }}>
                        Enable border by setting border width greater than 0
                    </small>
                )}
            </div>
            <div className="property-field">
                <label>Border Radius (px)</label>
                <input
                    type="range"
                    min="0"
                    max="20"
                    value={block.style.borderRadius !== undefined ? block.style.borderRadius : 8}
                    onChange={(e) => updateStyle('borderRadius', parseInt(e.target.value))}
                />
                <span>{block.style.borderRadius !== undefined ? block.style.borderRadius : 8}</span>
            </div>
            {!(block.elementType === 'header' || block.elementType === 'footer') && (
               <div className="property-field">
                   <label>Shadow</label>
                   <select
                       value={block.style.shadow || 'medium'}
                       onChange={(e) => updateStyle('shadow', e.target.value)}
                   >
                       <option value="none">None</option>
                       <option value="small">Small</option>
                       <option value="medium">Medium</option>
                       <option value="large">Large</option>
                   </select>
               </div>
            )}
     
            {/* Font Size and Color Controls for Widgets */}
            {block.type === 'widget' && (block.widgetType === 'numeric' || block.widgetType === 'statcard' || block.widgetType === 'gauge') && (
                <>
                    <h4 style={{ marginTop: '1.5rem', marginBottom: '0.75rem' }}>Value & Label Styling</h4>
                    <div className="property-field">
                        <label>Value Font Size (px)</label>
                        <input
                            type="number"
                            min="8"
                            max="72"
                            value={block.style.valueFontSize || 24}
                            onChange={(e) => updateStyle('valueFontSize', parseInt(e.target.value) || 24)}
                        />
                    </div>
                    <div className="property-field">
                        <label>Value Color</label>
                        <input
                            type="color"
                            value={block.style.valueColor || '#000000'}
                            onChange={(e) => updateStyle('valueColor', e.target.value)}
                        />
                    </div>
                    <div className="property-field">
                        <label>Label Font Size (px)</label>
                        <input
                            type="number"
                            min="8"
                            max="48"
                            value={block.style.labelFontSize || 14}
                            onChange={(e) => updateStyle('labelFontSize', parseInt(e.target.value) || 14)}
                        />
                    </div>
                    <div className="property-field">
                        <label>Label Color</label>
                        <input
                            type="color"
                            value={block.style.labelColor || '#666666'}
                            onChange={(e) => updateStyle('labelColor', e.target.value)}
                        />
                    </div>
                </>
            )}



            {/* Background Color Variable Selection - Moved or removed if handled elsewhere */}
        </div>
    );
};

export default StyleTab;
